<template>
  <div class="rep-callback-form">
    <div
      ref="content"
      class="rep-callback-form__content"
      js-overlay="scrollElement"
    >
      <p
        class="text-heading-xl text-heading-2xl-desktop"
        v-text="$string('general.find_rep.callback.title')"
      />

      <p
        class="text-body-s-regular"
        v-text="$string('general.find_rep.callback.body')"
      />

      <div class="form-group">
        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="name"
            v-text="$string('general.find_rep.callback.name_label')"
          />

          <input
            id="name"
            v-model="name"
            autocapitalize="off"
            autocomplete="name"
            autocorrect="off"
            :placeholder="$string('general.find_rep.callback.name_placeholder')"
            spellcheck="false"
            type="text"
          >
        </div>

        <div
          ref="emailInput"
          class="form-group__field"
          :class="{[$class('error')]: notValidEmail}"
        >
          <label
            class="form-group__label text-body-s-bold"
            for="email"
            v-text="$string('general.find_rep.callback.email_label')"
          />

          <input
            id="email"
            v-model="email"
            autocapitalize="off"
            autocomplete="email"
            autocorrect="off"
            :placeholder="$string('general.find_rep.callback.email_placeholder')"
            spellcheck="false"
            type="email"
            @input="notValidEmail = false"
          >

          <p
            v-if="notValidEmail"
            class="rep-callback-form__error text-body-s-regular"
            v-text="$string('general.find_rep.callback.email_error')"
          />
        </div>

        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="school"
            v-text="$string('general.find_rep.callback.school_label')"
          />

          <input
            id="school"
            v-model="school"
            autocapitalize="off"
            autocorrect="off"
            :placeholder="$string('general.find_rep.callback.school_placeholder')"
            spellcheck="false"
            type="text"
          >
        </div>

        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="tel"
            v-text="$string('general.find_rep.callback.phone_label')"
          />

          <input
            id="tel"
            v-model="tel"
            autocapitalize="off"
            autocomplete="tel"
            autocorrect="off"
            :placeholder="$string('general.find_rep.callback.phone_placeholder')"
            spellcheck="false"
            type="tel"
          >
        </div>

        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="postalCode"
            v-text="$string('general.find_rep.callback.postcode_label')"
          />

          <input
            id="postalCode"
            v-model="postalCode"
            autocomplete="shipping postal-code"
            autocorrect="off"
            autocapitalize="characters"
            :placeholder="$string('general.find_rep.callback.postcode_placeholder')"
            spellcheck="false"
            type="text"
          >
        </div>

        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="query"
            v-text="$string('general.find_rep.callback.query_label')"
          />

          <textarea
            id="query"
            v-model="query"
            :placeholder="$string('general.find_rep.callback.query_placeholder')"
            rows="4"
            maxlength="100"
            spellcheck="false"
          />
        </div>

        <div class="form-group__field">
          <label
            class="form-group__label text-body-s-bold"
            for="time"
            v-text="$string('general.find_rep.callback.time_label')"
          />

          <textarea
            id="time"
            v-model="time"
            :placeholder="$string('general.find_rep.callback.time_placeholder')"
            rows="4"
            maxlength="100"
            spellcheck="false"
          />
        </div>
      </div>
    </div>

    <div class="rep-callback-form__actions">
      <btn
        :label="$string('general.find_rep.callback.submit_label')"
        modifiers="primary block center"
        @click="handleFormSubmit"
      />

      <btn
        :label="$string('general.find_rep.callback.cancel_label')"
        modifiers="secondary block center"
        @click="closeOverlay('repCallback')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Rep callback form (rep-callback-form)
 * -----------------------------------------------------------------------------
 * Rep callback request form.
 *
 * @param {Object} rep - Rep data object.
 *
 */

import { mapActions } from 'vuex'
import { validateEmail } from '~/helpers/general'
import Btn from '~global/btn/btn'

export default {
  name: 'RepCallbackForm',

  components: {
    Btn,
  },

  props: {
    rep: {
      type: Object,
      default: () => ({
        name: '',
        title: '',
        phone: '',
        email: '',
      }),
    },
  },

  data() {
    return {
      name: '',
      email: '',
      school: '',
      tel: '',
      postalCode: '',
      query: '',
      time: '',
      notValidEmail: false,
    }
  },

  computed: {
    emailBody() {
      const body = `${this.$string('general.find_rep.callback.name_label')}: ${this.name}
        ${this.$string('general.find_rep.callback.email_label')}: ${this.email}
        ${this.$string('general.find_rep.callback.school_label')}: ${this.school}
        ${this.$string('general.find_rep.callback.phone_label')}: ${this.tel}
        ${this.$string('general.find_rep.callback.postcode_label')}: ${this.postalCode}
        ${this.$string('general.find_rep.callback.query_label')}: ${this.query}
        ${this.$string('general.find_rep.callback.time_label')}: ${this.time}
      `

      return encodeURIComponent(body)
    },

    mailToUrl() {
      const email = this.rep.email
      const subject = this.$string('general.find_rep.callback.title')
      const body = this.emailBody

      return `mailto:${email}?subject=${subject}&body=${body}`
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    handleFormSubmit() {
      const validEmail = validateEmail(this.email)
      if (!validEmail) {
        this.notValidEmail = true
        this.$refs.emailInput?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        return
      }

      window.location = this.mailToUrl

      /**
       * Clear form scroll to top of form and close the modal.
       */
      setTimeout(() => {
        this.name = ''
        this.email = ''
        this.school = ''
        this.tel = ''
        this.postalCode = ''
        this.query = ''
        this.time = ''

        this.$refs.content.scrollTop = 0
        this.closeOverlay('repCallback')
      }, 1000)
    },
  },
}
</script>

<style lang="scss">
@import './rep-callback-form';
</style>
